import React, { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { BryntumGantt, BryntumGanttProps } from '@bryntum/gantt-react';
import { ColumnStore, DateHelper, Gantt, PresetManager, ProjectModel, Store } from '@bryntum/gantt';
import { debounce, isNil, round, uniq } from 'lodash';
import AlertTaskContext from '../contexts/AlertTaskContext';
import { getRandomString } from '../utils/commonUtil';
import '@bryntum/gantt/gantt.stockholm.css';
import { App, Button, Card, Col, Flex, Input, InputNumber, Modal, Row, Select, Space, Switch, Table, Transfer } from 'antd';
import { getDayString } from '../utils/DateUtil';
import { CloseOutlined } from '@ant-design/icons';
import GanttSubChart from './bryntum-gantt-sub';
import RelatedPathContext from '../contexts/RelatedPathContext';
import PathMpPeak from './path-mp-peak';
import { useTranslation } from 'react-i18next';
import { updateTasksCheckStatus } from './bryntum-gantt-with-mp/date-range-mapper';
import dayjs from 'dayjs';
const COLUMN_ORDER_KEY = 'gantt-column-order';
PresetManager.registerPreset('monthAndYear', {
  base: 'monthAndYear',
  tickWidth: 70, // 设置宽度
  headers: [
    {
      unit: 'YEAR',
      align: 'center',
      dateFormat: 'Y',
    },
    {
      unit: 'MONTH',
      align: 'center',
      dateFormat: 'MMM',
    },
  ],
});
const defaultColumns = [
  {
    type: 'check',
    field: 'check',
    text: 'Pick',
    width: 80,
    filterable: {
      filterField: {
        type: 'combo',
        multiSelect: true,
        editable: false,
        // items: {
        //   true: 'Checked',
        //   false: 'UN Checked',
        // },
        items: [
          // { value: null, text: 'All' },
          { value: true, text: 'Checked' },
          { value: false, text: 'Unchecked' },
        ],
      },
    },
    checkCls: 'bryntum-column-check',
  },
  {
    type: 'name',
    field: 'name',
    width: 320,
    editor: false,
    filterable: ({ value, record }) => {
      const names = value.split(' ').filter((n) => !!n);
      return !!record.name && names.some((name) => record.name.toUpperCase().includes(name.trim().toUpperCase()));
    },
  },
  {
    text: 'Activity ID',
    field: 'taskId',
    width: 160,
    renderer: (task) => `${task.record.taskId || '-'}`,
    editor: false,
    filterable: ({ value, record }) => {
      const ids = value.split(' ').filter((n) => !!n);
      return !!record.taskId && ids.some((id) => record.taskId.toUpperCase().includes(id.trim().toUpperCase()));
    },
  },
  {
    text: 'wbs',
    field: 'wbs',
    renderer: (task) => `${task.record.wbs || '-'}`,
    editor: false,
    filterable: ({ value, record }) => {
      const wbs = value.split(' ').filter((n) => !!n);
      return !!record.wbs && wbs.some((w) => record.wbs.toUpperCase().includes(w.trim().toUpperCase()));
    },
  },
  {
    text: 'discipline',
    field: 'discipline',
    renderer: (task) => `${task.record.discipline || '-'}`,
    sortable: false,
    filterable: {
      filterField: {
        type: 'combo',
        multiSelect: true,
        editable: false,
        items: [],
      },
    },
    editor: false,
  },
  {
    text: 'CWA',
    field: 'area',
    renderer: (task) => `${task.record.area || '-'}`,
    sortable: false,
    filterable: {
      filterField: {
        type: 'combo',
        multiSelect: true,
        editable: false,
        items: [],
      },
    },
    editor: false,
  },
  {
    text: 'package',
    field: 'package',
    renderer: (task) => `${task.record.package || '-'}`,
    sortable: false,
    filterable: {
      filterField: {
        type: 'combo',
        multiSelect: true,
        editable: false,
        items: [],
      },
    },
    editor: false,
  },
  { type: 'startdate', editor: false },
  { type: 'enddate', editor: false },
  {
    text: 'duration',
    field: 'durOrg',
    editor: true,
    filterable: ({ value, record }) => {
      const durs = value
        .split(' ')
        .filter((n) => !!n)
        .map((d) => Number(d.trim()))
        .filter((d) => !Number.isNaN(d));
      return !!record.durOrg && durs.some((dur) => record.durOrg.toString().includes(dur.toString()));
    },
  },
  // { type: 'duration', editor: false },
  {
    type: 'predecessor',
    editor: false,
    filterable: ({ value, record }) => {
      const pds = value.split(' ').filter((n) => !!n);
      const predecessorsIds = record.predecessors.map((p) => p.from).join(';');
      return !!predecessorsIds && pds.some((pd) => predecessorsIds.toUpperCase().includes(pd.trim().toUpperCase()));
    },
  },
];
const getSavedColumnOrder = (): string[] => {
  const savedOrder = localStorage.getItem(COLUMN_ORDER_KEY);
  return savedOrder ? JSON.parse(savedOrder) : null;
};

// 保存列顺序到localStorage
const saveColumnOrder = (columnOrder: string[]): void => {
  localStorage.setItem(COLUMN_ORDER_KEY, JSON.stringify(columnOrder));
};
const getOrderedColumns = (): any[] => {
  const savedOrder = getSavedColumnOrder();
  if (!savedOrder) {
    return defaultColumns;
  }

  const columnMap = new Map();
  defaultColumns.forEach((col) => {
    const key = col.field || col.type;
    columnMap.set(key, col);
  });

  const orderedColumns = [];
  savedOrder.forEach((field) => {
    if (columnMap.has(field)) {
      orderedColumns.push(columnMap.get(field));
    }
  });

  return orderedColumns.length > 0 ? orderedColumns : defaultColumns;
};
const ganttConfig: BryntumGanttProps = {
  selectionMode: {
    multiSelect: false,
    dragSelect: false,
  },
  // appendTo: 'main',
  // flex: '1 1 auto',
  scrollTaskIntoViewOnCellClick: true,
  subGridConfigs: {
    fixed: {
      // No resizing of the fixed column region
      resizable: false,
      // Set a lower weight than the built-in "locked" section to have it appear to the left
      weight: 1,
    },
    locked: {
      // A scrollable section with the main columns
      width: 320,
      weight: 2,
    },
  },
  columns: getOrderedColumns(),
  barMargin: 5,
  rowHeight: 20,
  cls: 'gantt-bar',
  project: {
    // startDate: '2023-03-01',
    // endDate: '2024-12-09',
    taskStore: {
      fields: [
        'check',
        'name',
        'taskId',
        'wbs',
        'discipline',
        'area',
        'package',
        'startdate',
        'enddate',
        'duration',
        'predecessor',
        'fwbsLevel',
        'cstrDate',
        'mpPerDay',
        'durOrg',
      ],
    },
    // transport: {
    //   load: {
    //     // url: '/api/schedule-tasks',
    //     url: '../data/gantt-data.json',
    //   },
    // },
    // autoLoad: false,
    // The State TrackingManager which the UndoRedo widget in the toolbar uses
    stm: {
      autoRecord: true,
    },
    // Reset Undo / Redo after each load
    resetUndoRedoQueuesAfterLoad: true,
    // calendarsData: [
    //   {
    //     id: 'general',
    //     name: 'General',
    //     intervals: [
    //       {
    //         recurrentStartDate: 'on Sat at 0:00',
    //         recurrentEndDate: 'on Mon at 0:00',
    //         isWorking: false,
    //       },
    //     ],
    //   },
    // ],
  },
  viewPreset: 'monthAndYear',
  dependenciesFeature: {
    allowCreate: false,
    drawOnScroll: false,
  },
  percentBarFeature: {
    allowResize: false,
  },
  taskEditFeature: false,
  taskMenuFeature: false,
  taskDragFeature: true,
  taskResizeFeature: true,
  taskDragCreateFeature: false,
  criticalPathsFeature: {
    disabled: true,
  },
  filterFeature: true,
  progressLineFeature: {
    disabled: true,
  },
  taskNonWorkingTimeFeature: true,
  baselinesFeature: {
    disabled: false,
    template: (item): string => {
      const { baseline } = item;
      const { task } = baseline;

      const displayDuration = baseline.duration;

      return `<div class="b-gantt-task-title">
              ${task.name} (${baseline.name})</div>
              <table>
                <tr>
                  <td>Start:</td>
                  <td>${item.startClockHtml}</td>
                </tr>
                <tr>
                  <td>End:</td>
                  <td>${item.endClockHtml}</td>
                </tr>
                <tr>
                  <td>Duration:</td>
                  <td class="b-right">${`${displayDuration} ${DateHelper.getLocalizedNameOfUnit(
                    baseline.durationUnit,
                    baseline.duration !== 1,
                  )}`}</td>
                </tr>
              </table>
              `;
    },
  },
  timeRangesFeature: true,
  indicatorsFeature: {
    items: {
      earlyDates: false,
      lateDates: false,
      deadlineDate: false,
      constraintDate: false,
      cstrDateIndicator: (taskRecord) =>
        taskRecord.cstrDate && ['MIL-', 'ROS-', 'ERD-'].some((startKey) => taskRecord.taskId.startsWith(startKey))
          ? {
              startDate: new Date(taskRecord.cstrDate),
              name: 'Constraint Date',
              iconCls: 'b-cus-diamond',
            }
          : null,
    },
    tooltipTemplate: (data) => {
      const {
        indicator: {
          taskRecord: { name, startDate, endDate, duration, percentDone },
        },
        startText,
      } = data;
      return `<div>
        <div style="font-weight: bolder">${name}</div>
        <div style="display: flex; justify-content: space-between;">
          <div>
            ${!isNil(startDate) ? `<div>Start: </div>` : ''} 
            ${!isNil(endDate) && startDate.getTime() !== endDate.getTime() ? `<div>End: </div>` : ''}
            ${!isNil(endDate) && startDate.getTime() !== endDate.getTime() ? `<div>Duration: </div>` : ''}
            ${!isNil(endDate) && startDate.getTime() !== endDate.getTime() ? `<div>Complete: </div>` : ''}
            <div>Constraint: </div>
          </div>
          <div style="margin-left: 5px; text-align: right">
            ${!isNil(startDate) ? `<div>${DateHelper.format(startDate, 'MMM DD, YYYY')}</div>` : ''} 
            ${
              !isNil(endDate) && startDate.getTime() !== endDate.getTime() ? `<div>${DateHelper.format(endDate, 'MMM DD, YYYY')}</div>` : ''
            } 
            ${!isNil(endDate) && startDate.getTime() !== endDate.getTime() ? `<div>${round(duration, 1)} days</div>` : ''}
            ${!isNil(endDate) && startDate.getTime() !== endDate.getTime() ? `<div>${percentDone * 100}%</div>` : ''}
            <div>${startText}</div>
          </div>
        </div>
      </div>`;
    },
  },
  // pdfExportFeature: {
  //   exportServer: 'http://localhost:8080',
  //   translateURLsToAbsolute: 'http://localhost:3000',
  //   clientURL: 'http://localhost:3000',
  //   keepPathName: false,
  // },
};

const findRelationChange = (taskId, depObj, res) => {
  const filtered = depObj.filter((dp) => dp.from === taskId);
  for (const dob of filtered) {
    const exist = res.find((r) => r.taskId === dob.toTask.taskId);
    if (exist) {
      continue;
    }
    res.push({
      taskId: dob.toTask.taskId,
      startDate: dob.toTask.startDate,
      endDate: dob.toTask.endDate,
      startDateStr: getDayString(dob.toTask.startDate),
      endDateStr: getDayString(dob.toTask.endDate),
    });
    if (dob.toTask.dependencies.length) {
      findRelationChange(dob.toTask.taskId, dob.toTask.dependencies, res);
    }
  }
};

const typeList: any = {
  0: 'SS',
  1: 'SF',
  2: 'FS',
  3: 'FF',
};

const typeOptions = Object.entries(typeList).map(([k, v]) => ({ label: v, value: Number(k) }));

const initializeOperationHistory = () => ({
  curIndex: 0,
  history: [],
});

// 由于历史操作记录属于甘特图组件的内部状态，状态提升的话不太合理
// 最终选择使用 forwardRef, 通过 ref 来获取子组件对应的状态
const GanttChart: React.FC<any> = forwardRef((props, ref) => {
  const { t } = useTranslation();

  const { message } = App.useApp();
  const {
    height,
    data,
    modeOperate,
    saveActiveTask,
    optimize,
    optimizationId,
    calenderOrg,
    setData,
    updateTaskScheduleInfo,
    optimizationAction,
    isMasterOptimization,
    openSaveFilterModal,
    openLoadFilterModal,
  } = props;
  const [mode, modeDispatch] = modeOperate;
  const gantt = useRef<any>(null);
  const ganttSub = useRef<any>(null);
  const [ganttKey, setGanttKey] = useState<string>();
  const { alertView, setAlertView } = useContext(AlertTaskContext);

  const ganttInstance = (): Gantt => gantt.current?.instance;
  const ganttProject = (): ProjectModel => ganttInstance().project;
  const ganttStore = (): Store => ganttInstance().store as Store;
  const ganttColumns = () => ganttInstance().columns as ColumnStore;

  const [selected, setSelected] = useState<any>();

  const projectReady = useRef(false);

  const [relatedPathOpen, setRelatedPathOpen] = useState(false);
  const [drivingOnly, setDrivingOnly] = useState(false);
  const [subGanttModalReady, setSubGanttModalReady] = useState(false);

  const [calenderData, setCalenderData] = useState<any[]>();

  // 新增状态：控制穿梭框弹窗
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [transferData, setTransferData] = useState<any[]>([]);
  const [targetKeys, setTargetKeys] = useState<string[]>([]);

  const [ganttLock, setGanttLock] = useState(false);

  // 初始化穿梭框数据
  const initTransferData = () => {
    const allColumns = [
      { key: 'name', title: 'Name' },
      { key: 'taskId', title: 'Activity ID' },
      { key: 'wbs', title: 'WBS' },
      { key: 'discipline', title: 'Discipline' },
      { key: 'area', title: 'CWA' },
      { key: 'package', title: 'Package' },
      { key: 'startdate', title: 'Start Date' },
      { key: 'enddate', title: 'End Date' },
      { key: 'duration', title: 'Duration' },
      { key: 'predecessor', title: 'Predecessor' },
    ];

    setTransferData(allColumns);

    const savedOrder = getSavedColumnOrder();
    if (savedOrder) {
      const userKeys = savedOrder.filter((key) => key !== 'check');
      setTargetKeys(userKeys);
    } else {
      setTargetKeys(allColumns.map((item) => item.key));
    }
  };

  const handleSaveTransfer = () => {
    const newOrder = ['check', ...targetKeys];

    saveColumnOrder(newOrder);

    setGanttCardConfig((prevConfig) => ({
      ...prevConfig,
      columns: getOrderedColumns(),
    }));

    setGanttKey(getRandomString());
    setTransferModalOpen(false);
  };

  // 打开穿梭框弹窗
  const handleOpenTransferModal = () => {
    initTransferData();
    setTransferModalOpen(true);
  };

  const getCurrentGanttConfig = (): BryntumGanttProps => ({
    ...ganttConfig,
    columns: getOrderedColumns(),
  });
  useEffect(() => {
    setGanttCardConfig(getCurrentGanttConfig());
  }, [optimizationId]);
  // 保存穿梭框选择的列顺序

  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === COLUMN_ORDER_KEY) {
        // localStorage中的列顺序发生变化，更新甘特图
        setGanttCardConfig(getCurrentGanttConfig());
        setGanttKey(Date.now().toString());
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);
  const handleCancelTransfer = () => {
    // 重新初始化数据，恢复到打开时的状态
    initTransferData();
    setTransferModalOpen(false);
  };
  const onFeaturesClick = ({ source: item }) => {
    if (item.feature) {
      const feature = ganttInstance().features[item.feature];
      feature.disabled = !feature.disabled;
    } else if (item.subGrid) {
      const subGrid = ganttInstance().subGrids[item.subGrid];
      subGrid.collapsed = !subGrid.collapsed;
    } else if (item.toggleConfig) {
      ganttInstance[item.toggleConfig] = item.checked;
    }
  };

  const onFeaturesShow = ({ source: menu }) => {
    menu.items.map((item) => {
      const { feature } = item;

      if (feature) {
        // a feature might be not presented in the gantt
        // (the code is shared between "advanced" and "php" demos which use a bit different set of features)
        if (ganttInstance().features[feature]) {
          item.checked = !ganttInstance().features[feature].disabled;
        } else {
          // hide not existing features
          item.hide();
        }
      } else if (item.subGrid) {
        item.checked = ganttInstance().subGrids[item.subGrid].collapsed;
      }
    });
  };

  const onSettingsShow = ({ source: menu }) => {
    const { rowHeight, barMargin } = menu.widgetMap;

    rowHeight.value = ganttInstance().rowHeight;
    barMargin.value = ganttInstance().barMargin;
    barMargin.max = ganttInstance().rowHeight / 2 - 5;
  };

  const onRowHeightChange = ({ value, source }) => {
    ganttInstance().rowHeight = value;
    source.owner.widgetMap.barMargin.max = value / 2 - 5;
  };

  const onBarMarginChange = ({ value }) => {
    ganttInstance().barMargin = value;
  };

  const [ganttCardConfig, setGanttCardConfig] = useState(ganttConfig);

  const scrollToTask = (record) => {
    ganttInstance()
      .expandTo(record)
      .then(() => {
        try {
          ganttInstance()
            .scrollTaskIntoView(record, {
              animate: true,
              block: 'center',
              highlight: true,
              focus: true,
            })
            .then(() => {
              ganttInstance().selectRow(record);
            });
        } catch (error) {
          console.log(error);
        }
      });
  };

  useEffect(() => {
    if (!alertView || alertView.tab !== 'schedule' || !alertView.key) {
      return;
    }
    if (alertView.zoom) {
      ganttInstance().zoomTo({
        preset: 'monthAndYear',
        centerDate: new Date(alertView.startDate),
      });
      // ganttInstance().highlightTimeSpans([
      //   {
      //     startDate: new Date(alertView.startDate),
      //     endDate: new Date(alertView.endDate),
      //   },
      // ]);
    } else {
      const recordIndex = ganttStore().allRecords.findIndex((item: any) => item.taskId === alertView.key);
      if (recordIndex > -1) {
        scrollToTask(ganttStore().allRecords[recordIndex]);
      }
    }
  }, [alertView]);

  useEffect(() => {
    if (!data) {
      return;
    }
    setGanttKey(getRandomString());
  }, [data]);

  const [activeTask, setActiveTask] = useState(null);
  const [operateHistory, setOperateHistory] = useState(initializeOperationHistory);
  const { curIndex, history } = operateHistory;
  const generateRequestData = () => {
    // history 需要截取到对应步骤位置再进行处理
    const keepHistory = history.slice(0, curIndex ? curIndex : undefined);
    const { idCollection, dependenciesData, dropCollection } = keepHistory.reduce(
      (acc, cur) => {
        if (cur.operation === 'drop') {
          acc.dropCollection.push(cur.changes.map((c) => c.taskId));
        }
        if (['drop', 'resize', 'duration'].includes(cur.operation)) {
          acc.idCollection.push(cur.changes.map((c) => c.taskId));
        }
        if (cur.operation.startsWith('dep')) {
          acc.dependenciesData.push(cur.changes);
        }
        return acc;
      },
      {
        idCollection: [],
        dependenciesData: [],
        dropCollection: [],
      },
    );
    const dropIds = Array.from(new Set(dropCollection.flat()));
    const scheduleIds = Array.from(new Set(idCollection.flat()));
    const schedule = ganttStore()
      .allRecords.filter((r: any) => scheduleIds.includes(r.taskId))
      .map((r: any) => {
        const isDrop = dropIds.includes(r.taskId);
        return {
          taskId: r.taskId,
          startDate: dayjs(r.startDate).format('YYYY-MM-DD HH:mm:ss'),
          endDate: dayjs(r.endDate).format('YYYY-MM-DD HH:mm:ss'),
          duration: r.durOrg,
          cstrType: isDrop ? 'Start On' : null,
          cstrDate: isDrop ? dayjs(r.startDate).format('YYYY-MM-DD HH:mm:ss') : null,
        };
      });

    const dependency = dependenciesData.flat().reduce((acc, cur) => {
      // 查找记录中是否已经存在记录
      const exist = acc.find((a) => a.fromTaskId === cur.fromTaskId && a.toTaskId === cur.toTaskId);
      // 如果存在记录，则用新的记录覆盖旧的记录（包含更新以及删除）
      if (exist) {
        exist.dependencyId = !exist.dependencyId.startsWith('_') ? exist.dependencyId : cur.dependencyId;
        exist.type = cur.type;
        exist.lag = cur.lag;
        exist.add = cur.add;
        exist.update = cur.update;
        exist.del = cur.del;
        // 如果 depId 以 '_' 开头，并且 del = true, 则代表是新添加依赖，但是被删除掉了
        // 如果 depId 不以 '_' 开头，并且 add = true， 则代表是删除之后，添加了之前存在的依赖
        // 需要在列表中去掉
        if ((exist.dependencyId.startsWith('_') && exist.del) || (!exist.dependencyId.startsWith('_') && exist.add)) {
          acc = acc.filter((a) => a.dependencyId !== exist.dependencyId);
        }
      } else {
        acc.push({ ...cur });
      }
      return acc;
    }, []);
    const keepKeys = ['dependencyId', 'fromTaskId', 'toTaskId', 'type', 'lag', 'add', 'update', 'del'];
    dependency.forEach((d) => {
      if (d.dependencyId.startsWith('_')) {
        d.dependencyId = null;
      }
      const keys = Object.keys(d);
      keys.forEach((k) => {
        if (!keepKeys.includes(k)) {
          Reflect.deleteProperty(d, k);
        }
      });
    });
    return { schedule, dependency };
  };

  useEffect(() => {
    if (ganttLock) return;
    setGanttCardConfig((prevConfig) => ({
      ...prevConfig,
      tbar: {
        items: [
          {
            type: 'combo',
            text: 'FWBS Lv',
            editable: false,
            value: '',
            items: {
              '': t('aipskd.optionDetail.all'),
              'FWBS Level 1': 'FWBS Level 1',
              'FWBS Level 4': 'FWBS Level 4',
              'FWBS Level 5': 'FWBS Level 5',
            },
            onSelect: ({ source, record, records, userAction }) => {
              if (record.value) {
                ganttInstance().taskStore.filter({
                  filters: (task) => {
                    return task.fwbsLevel === record.value;
                  },
                  replace: true,
                });
              } else {
                ganttInstance().taskStore.clearFilters();
              }
            },
          },
          {
            type: 'buttonGroup',
            items: [
              {
                ref: 'expandAllButton',
                icon: 'b-fa b-fa-angle-double-down',
                tooltip: 'Expand all',
                onAction: () => {
                  ganttInstance().expandAll();
                },
              },
              {
                ref: 'collapseAllButton',
                icon: 'b-fa b-fa-angle-double-up',
                tooltip: 'Collapse all',
                onAction: () => {
                  ganttInstance().collapseAll();
                },
              },
            ],
          },
          {
            type: 'buttonGroup',
            items: [
              {
                ref: 'zoomInButton',
                icon: 'b-fa b-fa-search-plus',
                tooltip: 'Zoom in',
                onAction: () => {
                  ganttInstance().zoomIn();
                },
              },
              {
                ref: 'zoomOutButton',
                icon: 'b-fa b-fa-search-minus',
                tooltip: 'Zoom out',
                onAction: () => {
                  ganttInstance().zoomOut();
                },
              },
              {
                ref: 'zoomToFitButton',
                icon: 'b-fa b-fa-compress-arrows-alt',
                tooltip: 'Zoom to fit',
                onAction: () => {
                  ganttInstance().zoomTo({
                    preset: 'monthAndYear',
                  });
                },
              },
              {
                ref: 'previousButton',
                icon: 'b-fa b-fa-angle-left',
                tooltip: 'Previous time span',
                onAction: () => {
                  ganttInstance().shiftPrevious();
                },
              },
              {
                ref: 'nextButton',
                icon: 'b-fa b-fa-angle-right',
                tooltip: 'Next time span',
                onAction: () => {
                  ganttInstance().shiftNext();
                },
              },
            ],
          },
          {
            type: 'button',
            ref: 'featuresButton',
            icon: 'b-fa b-fa-tasks',
            text: 'Settings',
            tooltip: 'Toggle features',
            menu: {
              onItem: onFeaturesClick,
              onBeforeShow: onFeaturesShow,
              // "checked" is set to a boolean value to display a checkbox for menu items. No matter if it is true or false.
              // The real value is set dynamically depending on the "disabled" config of the feature it is bound to.
              items: [
                {
                  text: 'UI settings',
                  icon: 'b-fa-sliders-h',
                  menu: {
                    cls: 'settings-menu',
                    layoutStyle: {
                      flexDirection: 'column',
                    },
                    onBeforeShow: onSettingsShow,
                    defaults: {
                      type: 'slider',
                      showValue: true,
                    },
                    items: [
                      {
                        ref: 'rowHeight',
                        text: 'Row height',
                        min: 20,
                        max: 70,
                        onInput: onRowHeightChange,
                      },
                      {
                        ref: 'barMargin',
                        text: 'Bar margin',
                        min: 0,
                        max: 10,
                        onInput: onBarMarginChange,
                      },
                    ],
                  },
                },
                {
                  text: 'Draw relations',
                  feature: 'dependencies',
                  checked: false,
                },
                {
                  text: 'Project lines',
                  feature: 'projectLines',
                  checked: false,
                },
                {
                  text: 'Show column lines',
                  feature: 'columnLines',
                  checked: true,
                },
                {
                  text: 'Hide schedule',
                  cls: 'b-separator',
                  subGrid: 'normal',
                  checked: false,
                },
                {
                  text: 'Setting columns',
                  cls: 'b-separator',
                  subGrid: 'normal',
                  onItem: (event) => {
                    handleOpenTransferModal();
                  },
                },
              ],
            },
          },
          {
            type: 'button',
            text: 'Save',
            tooltip: 'Save Check Status',
            toggleable: true,
            onAction: (event) => {
              if (event.source.pressed) {
                saveActiveTask(
                  ganttStore()
                    .allRecords.filter((record: any) => record.check)
                    .map((record: any) => record.id),
                );
              }
            },
          },
          {
            type: 'button',
            text: 'Load Filter',
            onAction: () => {
              openLoadFilterModal();
            },
          },
          {
            type: 'button',
            text: 'Save Filter',
            onAction: () => {
              openSaveFilterModal();
            },
          },
          {
            key: 'Scheduling',
            type: 'button',
            text: 'Scheduling',
            disabled: history.slice(0, curIndex ? curIndex : undefined).length === 0,
            onClick: (event) => {
              const request = generateRequestData() as any;
              request.optimizationId = optimizationId;
              request.optimizeId = optimize.optimizeId;
              request.planId = optimize.planId;
              request.projectId = optimize.projectId;
              request.planName = optimize.planName;
              request.optimizeName = optimize.optimizeName;
              request.dataCrossSection = dayjs().format('YYYY-MM-DD');
              // 运行 argo 时，锁定甘特图操作功能
              setGanttLock(true);
              (ganttInstance().tbar.items as any[]).forEach((item) => {
                item.disabled = true;
              });
              (ganttInstance().columns as any).get('durOrg').editor = false;
              ganttInstance().features.taskDrag.disabled = true;
              ganttInstance().features.taskResize.disabled = true;
              // 提交数据成功后，清除历史记录信息
              updateTaskScheduleInfo(request, () => {
                // argo 运行完成后，解锁甘特图操作功能
                setGanttLock(false);
                setOperateHistory(initializeOperationHistory);
              });
            },
          },
          {
            key: 'Optimization',
            type: 'button',
            text: 'Create new plan',
            // disabled: history.length !== 0 || !isMasterOptimization,
            disabled: history.length !== 0,
            onClick: (event) => {
              // 执行 optimization 动作
              optimizationAction(optimizationId);
            },
          },
          {
            key: 'Prev',
            type: 'button',
            text: 'Prev',
            // 没有操作记录 或者 在 10 步操作范围之内
            disabled: !history.length || -(history.length > 10 ? 10 : history.length) > curIndex - 1,
            onClick: async (event) => {
              const { history, curIndex } = operateHistory;
              const op = history.at(curIndex - 1);
              const { operation, changes } = op;
              switch (operation) {
                case 'depUpdate':
                  {
                    // 找到修改的task及其dependency，修改为上一次的值
                    const { activeTaskId, fromTaskId, toTaskId, changeKey, prevValue } = changes[0];
                    const task = ganttStore().allRecords.find((r: any) => r.taskId === activeTaskId) as any;
                    const revertDependency = task.dependencies.find((d: any) => d.from === fromTaskId && d.to === toTaskId);
                    revertDependency[changeKey] = prevValue;
                    // 如果恢复的是当前 activeTask，则需要同时更新 table 的显示数据
                    if (task.taskId === activeTask.taskId) {
                      const dataInfo = processDependencies(task);
                      setPredecessorData(dataInfo.predecessor);
                      setSuccessorData(dataInfo.successor);
                    }
                  }
                  break;
                case 'depAdd':
                  {
                    // 找到被添加了 dependency 的 task
                    const { activeTaskId, dependencyId } = changes[0];
                    const task = ganttStore().allRecords.find((r: any) => r.taskId === activeTaskId) as any;
                    task.dependencyStore.remove(dependencyId);
                    // 如果恢复的是当前 activeTask，则需要同时更新 table 的显示数据
                    if (task.taskId === activeTask.taskId) {
                      const dataInfo = processDependencies(task);
                      setPredecessorData(dataInfo.predecessor);
                      setSuccessorData(dataInfo.successor);
                    }
                  }
                  break;
                case 'depDelete':
                  {
                    // 找到被删除 dependency 的 task
                    // 根据记录创建之前的 dependency 并添加到 task 中
                    const task = ganttStore().allRecords.find((r: any) => r.taskId === changes[0].activeTaskId) as any;
                    const addDeps = changes.map((c) => ({
                      id: c.dependencyId,
                      fromTask: c.fromTaskId,
                      lag: c.lag,
                      toTask: c.toTaskId,
                      type: c.type,
                    }));
                    await task.dependencyStore.addAsync(addDeps);
                    // 如果恢复的是当前 activeTask，则需要同时更新 table 的显示数据
                    if (task.taskId === activeTask.taskId) {
                      const dataInfo = processDependencies(task);
                      setPredecessorData(dataInfo.predecessor);
                      setSuccessorData(dataInfo.successor);
                    }
                  }
                  break;
                case 'resize':
                case 'drop':
                  {
                    const { taskId, oldStartDate, oldEndDate } = changes[0];
                    const task = ganttStore().allRecords.find((r: any) => r.taskId === taskId) as any;
                    task.startDate = oldStartDate;
                    task.endDate = oldEndDate;
                  }
                  break;
                case 'duration':
                  {
                    const { taskId, oldDurOrg } = changes[0];
                    const task = ganttStore().allRecords.find((r: any) => r.taskId === taskId) as any;
                    task.durOrg = oldDurOrg;
                  }
                  break;
                default:
                  break;
              }
              setOperateHistory((prev) => ({
                ...prev,
                curIndex: prev.curIndex - 1,
              }));
            },
          },
          {
            key: 'Next',
            type: 'button',
            text: 'Next',
            // 没有操作记录 或者 在最后一步
            disabled: !history.length || curIndex >= 0,
            onClick: async (event) => {
              const op = history.at(curIndex);
              const { operation, changes } = op;
              switch (operation) {
                case 'depUpdate':
                  {
                    // 找到修改的task及其dependency，修改为新一次的值
                    const { activeTaskId, fromTaskId, toTaskId, changeKey } = changes[0];
                    const task = ganttStore().allRecords.find((r: any) => r.taskId === activeTaskId) as any;
                    const revertDependency = task.dependencies.find((d: any) => d.from === fromTaskId && d.to === toTaskId);
                    revertDependency[changeKey] = changes[0][changeKey];
                    // 如果恢复的是当前 activeTask，则需要同时更新 table 的显示数据
                    if (task.taskId === activeTask.taskId) {
                      const dataInfo = processDependencies(task);
                      setPredecessorData(dataInfo.predecessor);
                      setSuccessorData(dataInfo.successor);
                    }
                  }
                  break;
                case 'depAdd':
                  {
                    // 找到被添加了 dependency 的 task
                    // 根据记录创建之前的 dependency 并添加到 task 中
                    const { activeTaskId, fromTaskId, toTaskId, type, lag, dependencyId } = changes[0];
                    const task = ganttStore().allRecords.find((r: any) => r.taskId === activeTaskId) as any;
                    await task.dependencyStore.addAsync({
                      id: dependencyId,
                      fromTask: fromTaskId,
                      lag: lag,
                      toTask: toTaskId,
                      type: type,
                    });
                    // 如果恢复的是当前 activeTask，则需要同时更新 table 的显示数据
                    if (task.taskId === activeTask.taskId) {
                      const dataInfo = processDependencies(task);
                      setPredecessorData(dataInfo.predecessor);
                      setSuccessorData(dataInfo.successor);
                    }
                  }
                  break;
                case 'depDelete':
                  {
                    // 找到被删除 dependency 的 task
                    // 根据记录将task 的 dependency 删除
                    const task = ganttStore().allRecords.find((r: any) => r.taskId === changes[0].activeTaskId) as any;
                    task.dependencyStore.remove(changes.map((c) => c.dependencyId));
                    // 如果恢复的是当前 activeTask，则需要同时更新 table 的显示数据
                    if (task.taskId === activeTask.taskId) {
                      const dataInfo = processDependencies(task);
                      setPredecessorData(dataInfo.predecessor);
                      setSuccessorData(dataInfo.successor);
                    }
                  }
                  break;
                case 'resize':
                case 'drop':
                  {
                    const { taskId, startDate, endDate } = changes[0];
                    const task = ganttStore().allRecords.find((r: any) => r.taskId === taskId) as any;
                    task.startDate = startDate;
                    task.endDate = endDate;
                  }
                  break;
                case 'duration':
                  {
                    const { taskId, durOrg } = changes[0];
                    const task = ganttStore().allRecords.find((r: any) => r.taskId === taskId) as any;
                    task.durOrg = durOrg;
                  }
                  break;
                default:
                  break;
              }
              setOperateHistory((prev) => ({
                ...prev,
                curIndex: prev.curIndex + 1,
              }));
            },
          },
          {
            key: 'relatedPath',
            type: 'button',
            text: 'Related Path',
            tooltip: 'Related Path',
            cls: 'related-path-btn',
            disabled: !activeTask,
            onAction: (event) => {
              setDrivingOnly(false);
              setRelatedPathOpen(true);
            },
          },
        ],
      },
    }));
  }, [optimizationId, ganttKey, operateHistory, activeTask, optimize, ganttLock]);

  const cachedResize = useRef(null);
  useEffect(() => {
    if (!gantt.current || !ganttKey) {
      return;
    }
    const setCheck = (tasks, checked = false) => {
      tasks.forEach((task) => {
        if (task.set) {
          task.set('check', checked);
        } else {
          task.check = checked;
        }
        if (task.children) {
          setCheck(task.children, checked);
        }
      });
    };

    const toggleEventHandler = ({ source, record, checked }) => {
      if (record.children) {
        setCheck(record.children, checked);
      }
      const task = updateTasksCheckStatus(data.tasksData, ganttStore().allRecords);
      setData({
        ...data,
        tasksData: task,
      });
    };
    ganttColumns().get('check').on('toggle', toggleEventHandler);
    ganttInstance().on('taskClick', ({ source, taskRecord, event }) => {
      taskClickHandler({ source, taskRecord, event });
    });
    ganttInstance().on('beforeTaskDrag', ({ source, taskRecord }) => {
      const { startDate, endDate, taskId } = taskRecord;
      cachedResize.current = {
        startDate,
        endDate,
        taskId,
      };
    });
    ganttInstance().on('afterTaskDrop', ({ source, taskRecords }) => {
      const { taskId, dependencies } = taskRecords[0];
      const changes = [];
      // findRelationChange(taskId, dependencies, changes);
      changes.unshift({
        taskId,
        startDate: taskRecords[0].startDate,
        endDate: taskRecords[0].endDate,
        oldStartDate: cachedResize.current?.startDate,
        oldEndDate: cachedResize.current?.endDate,
      });
      setOperateHistory((prev) => {
        const { curIndex, history } = prev;
        const newHistory = history.slice(0, curIndex ? curIndex : undefined);
        return {
          curIndex: 0,
          history: [
            ...newHistory,
            {
              operation: 'drop',
              changes,
            },
          ],
        };
      });
    });
    ganttInstance().on('taskResizeStart', ({ taskRecord }) => {
      const { startDate, endDate, taskId } = taskRecord;
      cachedResize.current = {
        startDate,
        endDate,
        taskId,
      };
    });
    ganttInstance().on('taskResizeEnd', ({ changed, taskRecord }) => {
      const { taskId, dependencies } = taskRecord;
      const changes = [];
      // findRelationChange(taskId, dependencies, changes);
      changes.unshift({
        taskId,
        startDate: taskRecord.startDate,
        endDate: taskRecord.endDate,
        oldStartDate: cachedResize.current?.startDate,
        oldEndDate: cachedResize.current?.endDate,
      });
      setOperateHistory((prev) => {
        const { curIndex, history } = prev;
        const newHistory = history.slice(0, curIndex ? curIndex : undefined);
        return {
          curIndex: 0,
          history: [
            ...newHistory,
            {
              operation: 'resize',
              changes,
            },
          ],
        };
      });
    });
    ganttInstance().on('startCellEdit', ({ grid, editorContext }) => {
      const { taskId, durOrg } = editorContext.record;
      cachedResize.current = {
        durOrg,
        taskId,
      };
    });
    ganttInstance().on('finishCellEdit', ({ grid, editorContext }) => {
      const { taskId, dependencies } = editorContext.record;
      const changes = [];
      // findRelationChange(taskId, dependencies, changes);
      const durOrg = Number.isNaN(Number(editorContext.record.durOrg)) ? 0 : Number(editorContext.record.durOrg);
      changes.unshift({
        taskId,
        durOrg,
        oldDurOrg: cachedResize.current?.durOrg,
      });
      setOperateHistory((prev) => {
        const { curIndex, history } = prev;
        const newHistory = history.slice(0, curIndex ? curIndex : undefined);
        return {
          curIndex: 0,
          history: [
            ...newHistory,
            {
              operation: 'duration',
              changes,
            },
          ],
        };
      });
    });
    ganttInstance().on(
      'selectionChange',
      ({ action, mode, source, deselected, selected, selecttion, deselectedCells, selectedCells, cellSelection }) => {
        const tbar = ganttInstance().tbar?.items as any[];
        if (tbar) {
          const relatedPathBtn = tbar.find((item) => item.key === 'relatedPath');
          relatedPathBtn.disabled = ganttInstance().selectedRecords.length !== 1;
          if (mode === 'row') {
            setSelected(selected[0]);
          }
        }
      },
    );
    projectReady.current = false;
    ganttProject().on({
      dataReady({ records }) {
        projectReady.current = true;
        const map = buildDependencyMap(ganttProject());
        dependencyMapRef.current = map;
      },
    });
    ganttProject()
      .loadInlineData(data)
      .then(() => {
        setTimeout(() => {
          if (gantt && gantt.current && ganttInstance) {
            const finalData = [];
            getDataFromTree(finalData, data.tasksData);
            const disciplineColumn: any = ganttColumns().get('discipline');
            disciplineColumn.filterable.filterField.items = uniq(
              finalData.filter((item) => item.discipline).map((item) => item.discipline),
            );
            const areaColumn: any = ganttColumns().get('area');
            areaColumn.filterable.filterField.items = uniq(finalData.filter((item) => item.area).map((item) => item.area));
            const packageColumn: any = ganttColumns().get('package');
            packageColumn.filterable.filterField.items = uniq(finalData.filter((item) => item.package).map((item) => item.package));
          }
        }, 1000);
        const lockedSubGrid = (ganttInstance().subGrids as any).locked;
        if (lockedSubGrid) {
          lockedSubGrid.width = 320;
        }
        ganttInstance().collapseAll();
      });
  }, [ganttKey]);

  const getDataFromTree = (finalData: any[], treeData: any[]) => {
    treeData.forEach((treeNode) => {
      if (treeNode.children && treeNode.children.length > 0) {
        getDataFromTree(finalData, treeNode.children);
      } else {
        finalData.push(treeNode);
      }
    });
  };

  const toTaskPosition = (taskId) => {
    setAlertView({
      key: taskId,
      tab: 'schedule',
    });
  };

  const [predecessorData, setPredecessorData] = useState([]);
  const [successorData, setSuccessorData] = useState([]);

  const processDependencies = (task: any) => {
    const { taskId, dependencies } = task;
    return dependencies.reduce(
      (acc, cur) => {
        if (cur.to === taskId) {
          const task = cur.fromTask;
          acc.predecessor.push({
            id: cur.id,
            taskId: task.taskId,
            taskName: task.name,
            startDate: getDayString(task.startDate),
            endDate: getDayString(task.endDate),
            dependencyType: cur.type,
            lag: cur.lag,
            freeFloat: cur.freeFloat,
            driving: cur.isDrivingPath ? 1 : 0,
          });
        }
        if (cur.from === taskId) {
          const task = cur.toTask;
          acc.successor.push({
            id: cur.id,
            taskId: task.taskId,
            taskName: task.name,
            startDate: getDayString(task.startDate),
            endDate: getDayString(task.endDate),
            dependencyType: cur.type,
            lag: cur.lag,
            freeFloat: cur.freeFloat,
            driving: cur.isDrivingPath ? 1 : 0,
          });
        }
        return acc;
      },
      {
        predecessor: [],
        successor: [],
      },
    );
  };

  const cachePrev = useRef([]);
  const debounceHistory = debounce(({ changeDep, fromTask, toTask, key }) => {
    const prevValue = cachePrev.current[0];
    // 将更新存储到修改记录中
    setOperateHistory((prev) => {
      const { curIndex, history } = prev;
      const newHistory = history.slice(0, curIndex ? curIndex : undefined);
      return {
        curIndex: 0,
        history: [
          ...newHistory,
          {
            operation: 'depUpdate',
            changes: [
              {
                dependencyId: changeDep.id,
                type: changeDep.type,
                lag: changeDep.lag,
                fromTaskId: fromTask,
                toTaskId: toTask,
                changeKey: key,
                prevValue,
                activeTaskId: activeTask.taskId,
                add: false,
                update: true,
                del: false,
              },
            ],
          },
        ],
      };
    });
    cachePrev.current = [];
  }, 800);

  const handleDependenciesChange = useCallback(
    (fromTask: string, toTask: string, key: 'type' | 'lag', val: string | number) => {
      // 每次触发修改 dependencies, 修改依赖的值，并更新当前选择 task 的table 状态
      const changeDep = activeTask.dependencies.find((d) => d.from === fromTask && d.to === toTask);
      const prevValue = changeDep[key];
      cachePrev.current.push(prevValue);
      changeDep[key] = val;
      const dataInfo = processDependencies(activeTask);
      setPredecessorData(dataInfo.predecessor);
      setSuccessorData(dataInfo.successor);
      debounceHistory({ changeDep, fromTask, toTask, key });
    },
    [activeTask],
  );

  const predecessorColumns = useMemo(() => {
    return [
      {
        key: 'Predecessor',
        title: 'Predecessor',
        children: [
          {
            key: 'PredecessorTaskId',
            dataIndex: 'taskId',
            title: t('aipskd.optionDetail.taskId'),
            ellipsis: true,
            width: 130,
            render: (text, record, index) => (
              <Button
                size="small"
                style={{ padding: 0 }}
                type="link"
                className="link-ellipsis"
                onClick={() => {
                  toTaskPosition(text);
                }}
              >
                {text}
              </Button>
            ),
          },
          {
            key: 'PredecessorTaskName',
            dataIndex: 'taskName',
            title: t('aipskd.optionDetail.taskName'),
            ellipsis: true,
            width: 140,
          },
          {
            key: 'PredecessorStartDate',
            dataIndex: 'startDate',
            title: t('aipskd.optionDetail.taskStartDate'),
            ellipsis: true,
            width: 100,
          },
          {
            key: 'PredecessorEndDate',
            dataIndex: 'endDate',
            title: t('aipskd.optionDetail.taskEndDate'),
            ellipsis: true,
            width: 100,
          },
          {
            key: 'PredecessorDependencyType',
            dataIndex: 'dependencyType',
            title: 'Relation',
            ellipsis: true,
            width: 80,
            render: (val, item) => {
              const options = Object.entries(typeList).map(([k, v]) => ({ label: v, value: Number(k) }));
              return (
                <Select
                  options={options}
                  value={val}
                  onChange={(value) => handleDependenciesChange(item.taskId, activeTask?.taskId, 'type', value)}
                />
              );
            },
          },
          {
            key: 'PredecessorLag',
            dataIndex: 'lag',
            title: 'Lag',
            ellipsis: true,
            width: 60,
            render: (val, item) => (
              <Input
                value={val}
                onChange={(e) => {
                  if (Number.isNaN(Number(e.target.value))) return;
                  handleDependenciesChange(item.taskId, activeTask?.taskId, 'lag', e.target.value);
                }}
              />
            ),
          },
          {
            key: 'PredecessorFreeFloat',
            dataIndex: 'freeFloat',
            title: 'Free Float',
            ellipsis: true,
          },
          {
            key: 'PredecessorDriving',
            dataIndex: 'driving',
            title: 'Driving',
            ellipsis: true,
            filterMultiple: false,
            filters: [
              {
                text: 1,
                value: 1,
              },
              {
                text: 0,
                value: 0,
              },
            ],
            onFilter: (value, record) => record.driving === value,
          },
        ],
      },
    ];
  }, [activeTask]);

  const successorColumns = useMemo(() => {
    return [
      {
        key: 'Successor',
        title: 'Successor',
        children: [
          {
            key: 'SuccessorTaskId',
            dataIndex: 'taskId',
            title: t('aipskd.optionDetail.taskId'),
            ellipsis: true,
            width: 130,
            render: (text, record, index) => (
              <Button
                size="small"
                style={{ padding: 0 }}
                type="link"
                className="link-ellipsis"
                onClick={() => {
                  toTaskPosition(text);
                }}
              >
                {text}
              </Button>
            ),
          },
          {
            key: 'SuccessorTaskName',
            dataIndex: 'taskName',
            title: t('aipskd.optionDetail.taskName'),
            ellipsis: true,
            width: 140,
          },
          {
            key: 'SuccessorStartDate',
            dataIndex: 'startDate',
            title: t('aipskd.optionDetail.taskStartDate'),
            ellipsis: true,
            width: 100,
          },
          {
            key: 'SuccessorEndDate',
            dataIndex: 'endDate',
            title: t('aipskd.optionDetail.taskEndDate'),
            ellipsis: true,
            width: 100,
          },
          {
            key: 'SuccessorDependencyType',
            dataIndex: 'dependencyType',
            title: 'Ralation',
            ellipsis: true,
            width: 80,
            render: (val, item) => {
              return (
                <Select
                  options={typeOptions}
                  value={val}
                  onChange={(value) => handleDependenciesChange(activeTask?.taskId, item.taskId, 'type', value)}
                />
              );
            },
          },
          {
            key: 'SuccessorLag',
            dataIndex: 'lag',
            title: 'Lag',
            ellipsis: true,
            width: 60,
            render: (val, item) => {
              return (
                <Input
                  value={val}
                  onChange={(e) => {
                    if (Number.isNaN(Number(e.target.value))) return;
                    handleDependenciesChange(activeTask?.taskId, item.taskId, 'lag', e.target.value);
                  }}
                />
              );
            },
          },
          {
            key: 'SuccessorFreeFloat',
            dataIndex: 'freeFloat',
            title: 'Free Float',
            ellipsis: true,
          },
          {
            key: 'SuccessorDriving',
            dataIndex: 'driving',
            title: 'Driving',
            ellipsis: true,
            filterMultiple: false,
            filters: [
              {
                text: 1,
                value: 1,
              },
              {
                text: 0,
                value: 0,
              },
            ],
            onFilter: (value, record) => record.driving === value,
          },
        ],
      },
    ];
  }, [activeTask]);

  const taskClickHandler = ({ source, taskRecord, event }) => {
    if (!projectReady.current) {
      return;
    }
    if (taskRecord.children) {
      return;
    }

    modeDispatch({ type: 'showTaskCard' });
    setActiveTask(taskRecord);
    const dataInfo = processDependencies(taskRecord);
    setPredecessorData(dataInfo.predecessor);
    setSuccessorData(dataInfo.successor);
  };

  const containerStyle = {
    display: 'flex',
    border: '1px solid #f0f0f0',
    borderRadius: '0 0 4px 4px',
    padding: 6,
    overflow: 'hidden',
    width: '100%',
    flex: 1,
  };

  const titleClickHandler = () => {
    ganttSub.current.scrollToTask(selected);
  };

  const [relatedTasks, setRelatedTasks] = useState([]);

  useEffect(() => {
    if (calenderOrg) {
      const calenderData = calenderOrg.filter((calender) => calender.workingDay);
      setCalenderData(calenderData);
    }
  }, [calenderOrg]);

  const [pCheckList, setPCheckList] = useState([]);
  const [sCheckList, setSCheckList] = useState([]);
  const [addType, setAddType] = useState<'predecessor' | 'successor'>('predecessor');
  const [depModalShow, setDepModalShow] = useState(false);
  const [addDep, setAddDep] = useState({
    taskId: null,
    taskName: null,
    type: 0,
    lag: 0,
  });

  const handleAddDepChange = (field: string, value: any) => {
    const changes = {
      [field]: value,
    };
    if (field === 'taskId') {
      const selectedTask = ganttStore().allRecords.find((r: any) => r.taskId === value) as any;
      changes['taskName'] = selectedTask?.name;
    }
    setAddDep((prev) => ({ ...prev, ...changes }));
  };

  const handleDepModalOK = () => {
    if (!addDep.taskId) {
      setDepModalShow(false);
      return;
    }
    if (addType === 'predecessor') {
      // 获取依赖的构造函数，根据数据创建依赖 Model
      // predecessor from 是选择的 task，to 是当前的 task
      // 更新当前选择 task 的依赖
      const [dep] = activeTask.dependencyStore.add({
        freeFloat: 0,
        isDrivingPath: false,
        fromTask: addDep.taskId,
        lag: addDep?.lag,
        toTask: activeTask.taskId,
        type: addDep?.type,
      });
      // 对依赖的 table 数据进行更新
      const dataInfo = processDependencies(activeTask);
      setPredecessorData(dataInfo.predecessor);
      setSuccessorData(dataInfo.successor);
      // 添加履历
      setOperateHistory((prev) => {
        const { curIndex, history } = prev;
        const newHistory = history.slice(0, curIndex ? curIndex : undefined);
        return {
          curIndex: 0,
          history: [
            ...newHistory,
            {
              operation: 'depAdd',
              changes: [
                {
                  dependencyId: dep.id,
                  type: addDep.type,
                  lag: addDep.lag,
                  fromTaskId: addDep.taskId,
                  toTaskId: activeTask.taskId,
                  activeTaskId: activeTask.taskId,
                  add: true,
                  update: false,
                  del: false,
                },
              ],
            },
          ],
        };
      });
    }
    if (addType === 'successor') {
      // 获取依赖的构造函数，根据数据创建依赖 Model
      // successor from 是当前的 task，to 是选择的 task
      // 更新当前选择 task 的依赖
      const [dep] = activeTask.dependencyStore.add({
        freeFloat: 0,
        isDrivingPath: false,
        fromTask: activeTask.taskId,
        lag: addDep?.lag,
        toTask: addDep.taskId,
        type: addDep?.type,
      });
      // 对依赖的 table 数据进行更新
      const dataInfo = processDependencies(activeTask);
      setPredecessorData(dataInfo.predecessor);
      setSuccessorData(dataInfo.successor);
      // 添加履历
      setOperateHistory((prev) => {
        const { curIndex, history } = prev;
        const newHistory = history.slice(0, curIndex ? curIndex : undefined);
        return {
          curIndex: 0,
          history: [
            ...newHistory,
            {
              operation: 'depAdd',
              changes: [
                {
                  dependencyId: dep.id,
                  type: addDep.type,
                  lag: addDep.lag,
                  fromTaskId: activeTask.taskId,
                  toTaskId: addDep.taskId,
                  activeTaskId: activeTask.taskId,
                  add: true,
                  update: false,
                  del: false,
                },
              ],
            },
          ],
        };
      });
    }
    setDepModalShow(false);
  };

  useEffect(() => {
    if (activeTask) {
      setPCheckList([]);
      setSCheckList([]);
    }
  }, [activeTask]);

  const dependencyMapRef = useRef(null);
  const buildDependencyMap = (project: any) => {
    const predMap = new Map<string, string[]>();
    const succMap = new Map<string, string[]>();

    project.dependencyStore.forEach((dep: any) => {
      // dep.fromId -> predecessor, dep.toId -> successor
      if (!predMap.has(dep.to)) predMap.set(dep.to, []);
      if (!succMap.has(dep.from)) succMap.set(dep.from, []);

      predMap.get(dep.to)!.push(dep.from);
      succMap.get(dep.from)!.push(dep.to);
    });

    return { predMap, succMap };
  };

  const getDpIds = (startId: string, depMap: Map<string, string[]>): string[] => {
    const visited = new Set<string>();
    const queue = [startId];

    while (queue.length > 0) {
      const current = queue.shift()!;
      const deps = depMap.get(current) || [];
      for (const d of deps) {
        if (!visited.has(d)) {
          visited.add(d);
          queue.push(d);
        }
      }
    }

    return Array.from(visited);
  };

  const taskOptions = useMemo(() => {
    if (!activeTask) {
      return [];
    }
    const { predMap, succMap } = dependencyMapRef.current;
    const pIds = getDpIds(activeTask.id, predMap);
    const sIds = getDpIds(activeTask.id, succMap);
    return ganttStore()
      .allRecords.filter(
        (task: any) =>
          !task.children &&
          // task.package === activeTask.package &&
          // task.discipline === activeTask.discipline &&
          ![...pIds, ...sIds].includes(task.taskId),
      )
      .map((task: any) => ({ label: task.taskId, value: task.taskId }));
  }, [activeTask?.dependencies]);

  const handleOpenAddModal = (type: 'predecessor' | 'successor') => {
    setDepModalShow(true);
    setAddType(type);
    setAddDep({
      taskId: null,
      taskName: null,
      type: 0,
      lag: 0,
    });
  };

  const handleRemoveDep = (depCheckList: any[], type: 'predecessor' | 'successor') => {
    // 删除依赖
    const depIds = depCheckList.map((p) => p.id);
    let changes = [];
    if (type === 'predecessor') {
      changes = depCheckList.map((p) => ({
        dependencyId: p.id,
        type: p.dependencyType,
        lag: p.lag,
        fromTaskId: p.taskId,
        toTaskId: activeTask.taskId,
        activeTaskId: activeTask.taskId,
        del: true,
        add: false,
        update: false,
      }));
      setPCheckList([]);
    }
    if (type === 'successor') {
      changes = depCheckList.map((p) => ({
        dependencyId: p.id,
        type: p.dependencyType,
        lag: p.lag,
        fromTaskId: activeTask.taskId,
        toTaskId: p.taskId,
        activeTaskId: activeTask.taskId,
        del: true,
        add: false,
        update: false,
      }));
      setSCheckList([]);
    }
    activeTask.dependencyStore.remove(depIds);
    // 更新当前状态下的table展示
    const dataInfo = processDependencies(activeTask);
    setPredecessorData(dataInfo.predecessor);
    setSuccessorData(dataInfo.successor);
    // 添加操作记录
    setOperateHistory((prev) => {
      const { curIndex, history } = prev;
      const newHistory = history.slice(0, curIndex ? curIndex : undefined);
      return {
        curIndex: 0,
        history: [
          ...newHistory,
          {
            operation: 'depDelete',
            changes,
          },
        ],
      };
    });
  };

  useImperativeHandle(
    ref,
    () => ({
      getOperateHistory: () => operateHistory,
      clearHistory: () => setOperateHistory(initializeOperationHistory),
      ganttInstance: () => ganttInstance(),
      projectReadyRef: () => projectReady.current,
    }),
    [projectReady.current],
  );

  return (
    <>
      <div className="bryntum-gantt-container">
        <BryntumGantt
          onColumnDrop={({ source, column, insertBefore, newParent }) => {
            const columnOrder = newParent.children.map((i) => (!!i.data.type ? i.data.type : i.data.field)).slice(0, -1);
            localStorage.setItem(COLUMN_ORDER_KEY, JSON.stringify(columnOrder));
          }}
          key={ganttKey}
          ref={gantt}
          height={height}
          {...ganttCardConfig}
        />
        {mode.viewMode === 'table-card' && (
          <Card
            styles={{
              header: {
                minHeight: 42,
              },
              body: {
                height: 255,
              },
            }}
            title={
              <span
                style={{
                  cursor: 'pointer',
                }}
                onClick={() => {
                  toTaskPosition(activeTask?.taskId);
                }}
              >
                <Button style={{ color: '#1a4f99', fontWeight: '500', padding: 0 }} type="link" size="large">
                  {activeTask?.taskId}
                </Button>
              </span>
            }
            extra={
              <CloseOutlined
                onClick={() => {
                  modeDispatch({ type: 'closeTaskCard' });
                  setActiveTask(null);
                }}
              />
            }
          >
            <Row style={{ position: 'relative' }}>
              <Col span={12}>
                <Table
                  bordered
                  scroll={{
                    y: 152,
                  }}
                  size="small"
                  rowKey="taskId"
                  pagination={false}
                  columns={predecessorColumns}
                  dataSource={predecessorData}
                  rowSelection={{
                    hideSelectAll: true,
                    selectedRowKeys: pCheckList.map((p) => p.taskId),
                    onSelect: (record, selected) => {
                      if (selected) {
                        setPCheckList((prev) => [...prev, record]);
                      } else {
                        setPCheckList((prev) => prev.filter((p) => p.taskId !== record.taskId));
                      }
                    },
                  }}
                />
                <Space align="center" style={{ position: 'absolute', right: 16, top: 8 }}>
                  <Button
                    size="small"
                    onClick={() => {
                      handleOpenAddModal('predecessor');
                    }}
                  >
                    ADD
                  </Button>
                  <Button
                    disabled={!pCheckList.length}
                    size="small"
                    onClick={() => {
                      handleRemoveDep(pCheckList, 'predecessor');
                    }}
                  >
                    REMOVE
                  </Button>
                </Space>
              </Col>
              <Col span={12}>
                <Table
                  bordered
                  scroll={{
                    y: 152,
                  }}
                  rowKey="taskId"
                  size="small"
                  pagination={false}
                  columns={successorColumns}
                  dataSource={successorData}
                  rowSelection={{
                    hideSelectAll: true,
                    selectedRowKeys: sCheckList.map((p) => p.taskId),
                    onSelect: (record, selected) => {
                      if (selected) {
                        setSCheckList((prev) => [...prev, record]);
                      } else {
                        setSCheckList((prev) => prev.filter((p) => p.taskId !== record.taskId));
                      }
                    },
                  }}
                />
                <Space align="center" style={{ position: 'absolute', right: 16, top: 8 }}>
                  <Button
                    size="small"
                    onClick={() => {
                      handleOpenAddModal('successor');
                    }}
                  >
                    ADD
                  </Button>
                  <Button
                    size="small"
                    disabled={!sCheckList.length}
                    onClick={() => {
                      handleRemoveDep(sCheckList, 'successor');
                    }}
                  >
                    REMOVE
                  </Button>
                </Space>
              </Col>
            </Row>
          </Card>
        )}

        {/* 穿梭框弹窗 */}
        <Modal
          open={transferModalOpen}
          onCancel={handleCancelTransfer}
          footer={[
            <Button key="cancel" onClick={handleCancelTransfer}>
              cancel
            </Button>,
            <Button key="save" type="primary" onClick={handleSaveTransfer}>
              save
            </Button>,
          ]}
          width={800}
          // destroyOnClose={true} // 关闭时销毁组件，确保每次打开都是初始状态
        >
          <div style={{ padding: '20px 0' }}>
            <Transfer
              dataSource={transferData}
              targetKeys={targetKeys}
              onChange={(newTargetKeys) => {
                setTargetKeys(newTargetKeys as string[]);
              }}
              render={(item) => item.title}
              titles={['Hidden Columns', 'Visible Columns']}
              listStyle={{
                width: 300,
                height: 400,
              }}
            />
          </div>
        </Modal>

        <Modal
          destroyOnClose
          title={
            <Flex gap={16}>
              <span
                style={{
                  cursor: 'pointer',
                }}
                onClick={() => {
                  titleClickHandler();
                }}
              >
                {selected?.taskId} Related Path
              </span>
              <Switch
                checkedChildren={<>Driving Only</>}
                unCheckedChildren={<>Related Path</>}
                value={drivingOnly}
                onChange={(checked) => {
                  setDrivingOnly(checked);
                }}
              />
            </Flex>
          }
          width={document.body.clientWidth * 0.9}
          styles={{
            body: {
              height: document.body.clientHeight * 0.75,
            },
          }}
          open={relatedPathOpen}
          onCancel={() => {
            setRelatedPathOpen(false);
          }}
          footer={null}
          getContainer={() => document.querySelector('.aipskd')}
          afterOpenChange={(open) => {
            setSubGanttModalReady(open);
          }}
        >
          <RelatedPathContext.Provider
            value={{
              drivingOnly,
              selected,
              relatedTasks,
              setRelatedTasks,
            }}
          >
            {selected && relatedPathOpen && subGanttModalReady && (
              <Flex style={{ height: '100%' }} vertical>
                <div style={containerStyle}>
                  <GanttSubChart ref={ganttSub} isReady={subGanttModalReady} />
                </div>
                <div style={containerStyle}>
                  <PathMpPeak optimizeId={optimize?.id} calenderData={calenderData} />
                </div>
              </Flex>
            )}
          </RelatedPathContext.Provider>
        </Modal>
        <Modal
          width="60%"
          title="Add Dependency"
          open={depModalShow}
          onOk={() => {
            handleDepModalOK();
          }}
          onCancel={() => {
            setDepModalShow(false);
          }}
        >
          <Flex style={{ margin: '20px 0' }} justify="space-evenly">
            <Space>
              タスクID:
              <Select
                showSearch
                optionFilterProp="label"
                style={{ width: 240 }}
                options={taskOptions}
                value={addDep.taskId}
                onChange={(value) => {
                  handleAddDepChange('taskId', value);
                }}
              />
            </Space>
            <Space>
              タスク名:
              <Input style={{ width: 160 }} disabled value={addDep?.taskName} />
            </Space>
            <Space>
              Relation:
              <Select
                style={{ width: 160 }}
                options={typeOptions}
                value={addDep?.type}
                onChange={(value) => {
                  handleAddDepChange('type', value);
                }}
              />
            </Space>
            <Space>
              Lag:
              <InputNumber
                style={{ width: 60 }}
                precision={0}
                controls={false}
                value={addDep?.lag}
                onChange={(e) => {
                  handleAddDepChange('lag', e);
                }}
              />
            </Space>
          </Flex>
        </Modal>
      </div>
    </>
  );
});
export default GanttChart;
